import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { DDSLoader } from 'three/addons/loaders/DDSLoader.js';
import { parseGSC, decodeMesh } from './gsc.mjs';
import { createPlaytest } from './playtest.mjs';
import { createLibrary } from './library.mjs';
import { createSceneBindings } from './scene-bindings.mjs';

const canvas = document.querySelector('#canvas');
const status = document.querySelector('#status');
const error = document.querySelector('#error');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
renderer.setSize(innerWidth, innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
const scene = new THREE.Scene();
scene.background = new THREE.Color('#18202b');
const camera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.05, 10000);
const orbit = new OrbitControls(camera, canvas);
orbit.enableDamping = true;
const timer = new THREE.Timer();
timer.connect(document);
const group = new THREE.Group();
scene.add(group, new THREE.HemisphereLight(0xeaf3ff, 0x7c6652, 2.3));
const light = new THREE.DirectionalLight(0xffebd0, 2.5);
light.position.set(-80, 120, 60); scene.add(light);
const keys = new Set();
let bounds = null, speed = 12, lastStats = 0, renderedFrames = 0, flying = false, dragging = false;
let geometryCache = new Map(), materialCache = new Map(), textureCache = new Map();
const ddsLoader = new DDSLoader();
const report = { state: 'empty', scene: null, textures: 0, geometries: 0, instances: 0, skipped: [], frames: 0 };
window.sagaPC = { report, camera, scene, renderer, group, orbit };

function disposeScene() {
  group.clear();
  for (const geometry of geometryCache.values()) geometry.dispose();
  for (const entry of materialCache.values()) { entry.basic.dispose(); entry.lit.dispose(); }
  for (const texture of textureCache.values()) texture.dispose();
  geometryCache = new Map(); materialCache = new Map(); textureCache = new Map();
}
function textureFor(parsed, index) {
  if (index < 0 || !parsed.textures.has(index)) return null;
  if (textureCache.has(index)) return textureCache.get(index);
  const source = parsed.textures.get(index);
  const data = ddsLoader.parse(source.data, true);
  if (!data.format || !data.mipmaps.length || data.isCubemap) return null;
  let texture;
  if (data.format === THREE.RGBAFormat) {
    texture = new THREE.DataTexture(data.mipmaps[0].data, data.width, data.height, THREE.RGBAFormat);
    texture.flipY = false;
  } else {
    texture = new THREE.CompressedTexture(data.mipmaps, data.width, data.height, data.format);
  }
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = data.mipmapCount > 1 ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  texture.needsUpdate = true;
  textureCache.set(index, texture);
  return texture;
}
function materialFor(parsed, source, decoded) {
  const key = `${source.address}:${decoded.hasColors}`;
  if (materialCache.has(key)) return materialCache.get(key);
  const texture = textureFor(parsed, source.texture);
  const rgb = source.color.slice(0, 3).map(v => Math.max(0, Math.min(1, Number.isFinite(v) ? v : 1)));
  const color = new THREE.Color(...rgb);
  const alphaMode = source.alpha & 15;
  // PC diffuseColor.w is not global opacity: many opaque surfaces store zero.
  // Alpha comes from the texture and vertex stream in this renderer.
  const parameters = { color: texture ? new THREE.Color(1, 1, 1) : color, map: texture, side: THREE.FrontSide, vertexColors: decoded.hasColors,
    transparent: alphaMode >= 1 && alphaMode <= 3, opacity: 1,
    alphaTest: 0.015, depthWrite: ((source.alpha >>> 14) & 3) !== 1 };
  const entry = { basic: new THREE.MeshBasicMaterial(parameters), lit: new THREE.MeshLambertMaterial(parameters) };
  materialCache.set(key, entry);
  return entry;
}
function frameScene() {
  if (!bounds) return;
  playtest.setActive(false);
  setFlightMode(false);
  const center = bounds.getCenter(new THREE.Vector3()), size = bounds.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.y, size.z) * 0.65;
  orbit.target.copy(center);
  camera.position.copy(center).add(new THREE.Vector3(radius * 0.7, radius * 0.85, radius));
  camera.near = Math.max(0.02, radius / 10000); camera.far = Math.max(1000, radius * 20);
  camera.updateProjectionMatrix(); camera.lookAt(center); orbit.update();
  speed = Math.max(3, radius / 12);
}
function applyMode() {
  const mode = document.querySelector('#render-mode').value;
  group.traverse(mesh => {
    if (!mesh.isMesh) return;
    const entry = mesh.userData.materials;
    mesh.material = mode === 'lit' ? entry.lit : entry.basic;
    mesh.material.wireframe = mode === 'wire';
  });
}
async function loadScene(file) {
  if (!file) return;
  document.querySelector('#file').disabled = true;
  status.textContent = `Reading ${file.name}…`; error.textContent = '';
  try {
    report.state = 'loading';
    const parsed = parseGSC(await file.arrayBuffer());
    status.textContent = `Building ${parsed.summary.draws.toLocaleString()} draw instances…`;
    await new Promise(resolve => requestAnimationFrame(resolve));
    disposeScene(); report.skipped = [];
    for (let n = 0; n < parsed.draws.length; n++) {
      const draw = parsed.draws[n];
      const key = `${draw.mesh.address}:${draw.material.format}:${draw.material.surfaceUV}`;
      const decoded = decodeMesh(parsed, draw.mesh, draw.material);
      let geometry = geometryCache.get(key);
      if (!geometry) {
        geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(decoded.positions, 3));
        geometry.setAttribute('uv', new THREE.BufferAttribute(decoded.uvs, 2));
        geometry.setAttribute('color', new THREE.BufferAttribute(decoded.colors, 3));
        geometry.setIndex(new THREE.BufferAttribute(decoded.indices, 1));
        if (decoded.hasNormals) geometry.setAttribute('normal', new THREE.BufferAttribute(decoded.normals, 3));
        else geometry.computeVertexNormals();
        geometry.computeBoundingSphere();
        geometryCache.set(key, geometry);
      }
      const materials = materialFor(parsed, draw.material, decoded);
      const mesh = new THREE.Mesh(geometry, materials.basic);
      mesh.name = `PC mesh ${draw.mesh.address.toString(16)}`;
      mesh.matrixAutoUpdate = false; mesh.matrix.fromArray(draw.matrix);
      mesh.userData.materials = materials;
      mesh.userData.initialVisible = draw.visible !== false;
      mesh.userData.scriptOwned = draw.specialIndex !== undefined;
      mesh.userData.specialIndex = draw.specialIndex;
      mesh.visible = objectVisible(mesh);
      group.add(mesh);
      if (n % 150 === 0) await new Promise(resolve => requestAnimationFrame(resolve));
    }
    // The PC engine's world coordinates use the opposite X handedness.
    group.scale.set(-1, 1, 1); group.updateMatrixWorld(true);
    parsed.bindings = createSceneBindings(parsed, group.children);
    bounds = new THREE.Box3().setFromObject(group);
    if (bounds.isEmpty()) throw new Error('Scene has no drawable geometry.');
    report.state = 'rendered'; report.scene = file.name; report.summary = parsed.summary;
    report.textures = textureCache.size; report.geometries = geometryCache.size; report.instances = group.children.length;
    report.bounds = { min: bounds.min.toArray(), max: bounds.max.toArray() };
    document.querySelector('#frame').disabled = false; document.querySelector('#fly').disabled = false;
    frameScene(); applyMode();
    return parsed;
  } catch (e) {
    report.state = 'error'; error.textContent = e.message; console.error(e);
    status.textContent = 'Scene loading failed.';
    return false;
  } finally { document.querySelector('#file').disabled = false; }
}
const playtest = createPlaytest({ scene, camera, orbit, canvas,
  onError: e => { error.textContent = e.message; console.error(e); },
  onCameraMode: active => { if (active) setFlightMode(false); },
});
window.sagaPC.playtest = playtest;
const library = createLibrary({ scene, camera, playtest, loadScene, onError: e => { error.textContent = e.message; console.error(e); } });
window.sagaPC.library = library;
document.querySelector('#file').addEventListener('change', event => { library.clear(); playtest.clear(); loadScene(event.target.files[0]); });
document.querySelector('#character-files').addEventListener('change', async event => {
  const input = event.target; input.disabled = true; document.querySelector('#file').disabled = true; error.textContent = ''; playtest.clear();
  library.clear();
  try {
    const files = [...input.files], scenes = files.filter(file => /_PC\.GSC$/i.test(file.name) && !/^THINGS_PC\.GSC$/i.test(file.name));
    if (scenes.length > 1) throw new Error('Choose a playtest folder with one level scene.');
    const sceneFile = scenes[0];
    if (sceneFile && !await loadScene(sceneFile)) return;
    document.querySelector('#file').disabled = true;
    await playtest.load(files);
  } catch (e) { error.textContent = e.message; console.error(e); }
  finally { input.disabled = false; document.querySelector('#file').disabled = false; }
});
document.querySelector('#frame').addEventListener('click', frameScene);
document.querySelector('#fly').addEventListener('click', () => { playtest.setActive(false); setFlightMode(!flying); });
document.querySelector('#render-mode').addEventListener('change', applyMode);
function objectVisible(mesh) {
  const mode = document.querySelector('#scene-objects').value;
  return !mesh.userData.scriptOwned || mode === 'all' || (mode === 'defaults' && (mesh.userData.runtimeVisible ?? mesh.userData.initialVisible));
}
document.querySelector('#scene-objects').addEventListener('change', () => group.children.forEach(mesh => { mesh.visible = objectVisible(mesh); }));
document.querySelector('#fullscreen').addEventListener('click', () => document.documentElement.requestFullscreen().catch(e => { error.textContent = e.message; }));
document.querySelector('#collapse').addEventListener('click', () => document.querySelector('#panel').classList.toggle('collapsed'));
function setFlightMode(enabled) {
  flying = enabled; dragging = false; orbit.enabled = !enabled; keys.clear();
  report.cameraMode = enabled ? 'flight' : 'orbit';
  document.querySelector('#fly').textContent = enabled ? 'Exit flight' : 'Fly through';
  document.querySelector('#help').textContent = enabled ? 'WASD to fly · Drag mouse to look · Q/E down/up · Shift faster · Escape to exit' : 'Drag to orbit · Wheel to zoom · Right-drag to pan';
  if (enabled) canvas.focus();
  else orbit.target.copy(camera.position).add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(10));
}
canvas.addEventListener('pointerdown', e => { if (flying) { dragging = true; canvas.setPointerCapture(e.pointerId); } });
canvas.addEventListener('pointerup', () => { dragging = false; });
canvas.addEventListener('pointercancel', () => { dragging = false; });
canvas.addEventListener('pointermove', e => {
  if (!flying || !dragging) return;
  const rotation = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
  rotation.y -= e.movementX * 0.003;
  rotation.x = THREE.MathUtils.clamp(rotation.x - e.movementY * 0.003, -Math.PI / 2 + 0.01, Math.PI / 2 - 0.01);
  camera.quaternion.setFromEuler(rotation);
});
addEventListener('keydown', e => { if (flying) { if(e.code === 'Escape') {setFlightMode(false);return;} keys.add(e.code); if (['Space','ArrowUp','ArrowDown'].includes(e.code)) e.preventDefault(); } });
addEventListener('keyup', e => keys.delete(e.code));
addEventListener('blur', () => keys.clear());
addEventListener('resize', () => { camera.aspect = innerWidth / innerHeight; camera.updateProjectionMatrix(); renderer.setSize(innerWidth, innerHeight); });
renderer.setAnimationLoop(() => {
  timer.update();
  const dt = Math.min(timer.getDelta(), 0.05), distance = dt * speed * (keys.has('ShiftLeft') ? 4 : 1);
  playtest.update(dt);
  library.update(dt);
  if (flying) {
    const forward = camera.getWorldDirection(new THREE.Vector3());
    const right = new THREE.Vector3().crossVectors(forward, camera.up).normalize();
    camera.position.addScaledVector(forward, distance * (Number(keys.has('KeyW')) - Number(keys.has('KeyS'))));
    camera.position.addScaledVector(right, distance * (Number(keys.has('KeyD')) - Number(keys.has('KeyA'))));
    camera.position.y += distance * (Number(keys.has('KeyE')) - Number(keys.has('KeyQ')));
  } else orbit.update();
  renderer.render(scene, camera); renderedFrames++; report.frames = renderedFrames;
  if (report.state === 'rendered' && performance.now() - lastStats > 1000) {
    status.textContent = `${report.scene}\n${report.instances.toLocaleString()} instances · ${report.textures} textures\n${renderer.info.render.triangles.toLocaleString()} triangles visible · ${renderer.info.render.calls} draw calls`;
    lastStats = performance.now();
  }
});
